import { useCallback, useRef } from 'react';

import { isNavigateEvent } from './NavigateEvent';

export interface UseNavigateEventHandlerOptions {
  /**
   * Router push. Inject so tests can stub it and callers can pass
   * `useNavigate()` from react-router (or any equivalent) without
   * the hook taking a hard dependency on a specific router.
   */
  navigate: (url: string) => void;
  /**
   * Fires when the event matched `type === 'navigate'` but its
   * payload was structurally invalid. Optional — most callers can
   * ignore malformed navigate events; the panel keeps streaming.
   */
  onInvalidNavigate?: (event: unknown) => void;
}

/**
 * Returns a stable event-handler callback that the NDJSON stream
 * dispatches into. The callback:
 *
 * - Returns `true` when the event was a well-formed `navigate` and
 *   `navigate(url)` was invoked.
 * - Returns `false` for any other event type, so the caller can
 *   chain additional handlers (`feedback`, `confirmation_request`,
 *   etc.) without duplicating the dispatch table here.
 *
 * The chat panel mount lives at the layout level, so a router push
 * does not unmount the panel — "keep panel open during navigation"
 * falls out of the layout-level mount rather than needing explicit
 * re-open logic here.
 *
 * `navigate` and `onInvalidNavigate` are absorbed via refs so
 * callers can pass inline closures without triggering callback
 * churn on every render — the returned handler identity is stable.
 */
export function useNavigateEventHandler(
  options: UseNavigateEventHandlerOptions,
): (event: unknown) => boolean {
  const navigateRef = useRef(options.navigate);
  const onInvalidRef = useRef(options.onInvalidNavigate);

  navigateRef.current = options.navigate;
  onInvalidRef.current = options.onInvalidNavigate;

  return useCallback((event: unknown): boolean => {
    if (!isNavigateEvent(event)) {
      // Tag-only check: if the discriminator says navigate but the
      // structural guard rejected it, surface as invalid.
      if (
        typeof event === 'object' &&
        event !== null &&
        (event as { type?: unknown }).type === 'navigate'
      ) {
        onInvalidRef.current?.(event);
      }

      return false;
    }

    navigateRef.current(event.url);

    return true;
  }, []);
}
